import { useState } from 'react';
import { Info, ChevronDown, ChevronUp, Layers, BarChart3 } from 'lucide-react';
import { InfoButton } from '../common/Tooltip';
import { AssumptionGapTable } from './AssumptionGapTable';
import { SensitivityHeatmap } from './SensitivityHeatmap';
import { BreakevenAnalysis } from './BreakevenAnalysis';
import { TimeSeriesProjection } from './TimeSeriesProjection';
import { LTVCalculator } from './LTVCalculator';
import { PortfolioPnLSummary } from '../builder/PortfolioPnLSummary';
import { RiskSummary } from '../summary/RiskSummary';
import { InsightCard } from '../ai/InsightCard';
import { usePortfolioPnL } from '../../hooks/useCalculations';

export function FinancialModelTab() {
  const [showBreakeven, setShowBreakeven] = useState(false);
  const [viewMode, setViewMode] = useState<'single' | 'portfolio'>('portfolio');
  const [demandMode, setDemandMode] = useState<'ceiling' | 'split'>('split');
  const portfolio = usePortfolioPnL();

  return (
    <div>
      <InsightCard tabId="financial" />

      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={() => setViewMode('portfolio')}
          className={`flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${
            viewMode === 'portfolio'
              ? 'border-chart-teal/40 bg-chart-teal/10 text-chart-teal'
              : 'border-border text-secondary hover:text-primary'
          }`}
        >
          <Layers size={14} />
          Portfolio Analysis
        </button>
        <button
          onClick={() => setViewMode('single')}
          className={`flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${
            viewMode === 'single'
              ? 'border-chart-teal/40 bg-chart-teal/10 text-chart-teal'
              : 'border-border text-secondary hover:text-primary'
          }`}
        >
          <BarChart3 size={14} />
          Single-Product Analysis
        </button>
        <InfoButton helpKey="financialViewMode" size={14} />
      </div>

      <div className="flex items-start gap-3 px-4 py-3 rounded-lg mb-6 border" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
        <Info size={16} className="text-muted shrink-0 mt-0.5" />
        <p className="text-sm text-secondary leading-relaxed">
          {viewMode === 'portfolio'
            ? 'Portfolio view shows combined economics across all configured packages. Fixed costs are applied once. Use the Package Builder tab to configure individual packages.'
            : 'Single-product analysis applies conjoint cold-market demand rates to individual price points with full fixed costs. Use for comparing relative attractiveness across price points, not as sales forecasts.'}
        </p>
      </div>

      {viewMode === 'portfolio' && (
        <>
          <div className="mb-6">
            <PortfolioPnLSummary
              portfolio={portfolio}
              demandMode={demandMode}
              onToggleDemandMode={() => setDemandMode(m => m === 'ceiling' ? 'split' : 'ceiling')}
            />
          </div>
          <div className="hidden md:block mb-6">
            <TimeSeriesProjection />
          </div>
          <div className="hidden md:block mb-6">
            <LTVCalculator />
          </div>
        </>
      )}

      <div className="mb-6">
        <AssumptionGapTable viewMode={viewMode} />
      </div>

      <div className="mb-6">
        <RiskSummary />
      </div>

      <div className="hidden md:block mb-6">
        <SensitivityHeatmap viewMode={viewMode} demandMode={demandMode} />
      </div>

      <div>
        <button
          onClick={() => setShowBreakeven(!showBreakeven)}
          className="flex items-center gap-2 text-sm text-secondary hover:text-primary transition-colors mb-3"
        >
          {showBreakeven ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          {showBreakeven ? 'Hide' : 'Show'} Breakeven Analysis
        </button>
        {showBreakeven && <BreakevenAnalysis viewMode={viewMode} demandMode={demandMode} />}
      </div>
    </div>
  );
}
